<template>
    <div class="track mt-2" @click="playTrack()"
        @contextmenu="openContextMenu"
        :class="{ 'selected': isSelected }">
        <div class="wrapper">
            <div class="playbutt" :class="{ 'playbutt-visible': hideMenu || isFilePlaying }" style="font-size: 2.5rem; cursor: pointer;">
                <i class="bi bi-play-fill" v-if="!isFilePlaying" @click.stop="playTrack()"></i>
                <i class="bi bi-play-fill" v-if="isFilePlaying && !isPlaying" @click.stop="toggle()"></i>
                <i class="bi bi-pause-fill" v-if="isFilePlaying && isPlaying" @click.stop="toggle()"></i>
            </div>
            <div class="track_info">
                <strong class="track_name">
                    <i v-if="hasLocalCopy" class="bi bi-cloud-arrow-down track_local_icon"></i>
                    {{ file.name ?? file.filename }}
                </strong>
                <div class="track_chips">
                    <span class="chip_thing_two" v-if="file.upload_date">{{ formatDateShort(file.upload_date) }}</span>
                    <span v-if="hasNotes" class="chip_thing_two track_note"><i class="bi bi-sticky"></i> {{ file.notes }}</span>
                    <template v-else>
                        <span class="chip_thing_two" v-if="showBPM && file.bpm && (file.bpm != 'null')">{{ Math.round(file.bpm) }} BPM</span>
                        <span class="chip_thing_two" v-if="(file.key && file.mode) && (file.key != 'null') && (file.mode != 'null') && showKey">{{ file.key }} {{ file.mode }}</span>
                        <span class="chip_thing_two" v-if="file.lufs && (file.lufs != 'null') && showLUFS">{{ (file.lufs / 100).toFixed(1) }} LUFS</span>
                    </template>
                </div>
            </div>
            <div class="right">
                <div class="text_sec2 track_duration">{{ parseDuration(file.duration) }}</div>
                <ContextMenu ref="contextMenu" :teleport="true" v-if="!hideMenu && !isShared">
                    <template #trigger>
                        <i class="bi bi-three-dots"></i>
                    </template>
                    <ContextMenuItem icon="bi bi-info-circle" @click="goto()">
                        {{ $t('trackItem.more') }}
                    </ContextMenuItem>
                    <ContextMenuItem v-if="storageType == 'remote'" icon="bi bi-link-45deg" @click="copyPublicLink()">
                        {{ $t('trackItem.copyPublicLink') }}
                    </ContextMenuItem>
                    <ContextMenuItem
                        v-if="storageType == 'remote'"
                        icon="bi bi-cloud-download"
                        :disabled="isSavingToLocal || hasLocalCopy"
                        @click="saveToLocal()"
                    >
                        {{ hasLocalCopy ? $t('trackItem.savedLocally') : $t('trackItem.saveLocal') }}
                    </ContextMenuItem>
                    <ContextMenuItem
                        v-if="storageType == 'local'"
                        icon="bi bi-cloud-upload"
                        :disabled="isSavingToCloud || !isAuthorized"
                        @click="saveToCloud()"
                    >
                        {{ isSavingToCloud ? $t('trackItem.saving') : $t('trackItem.saveCloud') }}
                    </ContextMenuItem>
                    <ContextMenuItem icon="bi bi-trash" @click="deleteTrack(file.id)">
                        {{ $t('trackItem.deleteTrack') }}
                    </ContextMenuItem>
                    <ContextMenuItem
                        v-if="currentProjectId && isInCurrentProject"
                        icon="bi bi-arrow-up"
                        :disabled="!canMoveUp"
                        @click="moveUp()"
                    >
                        {{ $t('trackItem.moveUp') }}
                    </ContextMenuItem>
                    <ContextMenuItem
                        v-if="currentProjectId && isInCurrentProject"
                        icon="bi bi-arrow-down"
                        :disabled="!canMoveDown"
                        @click="moveDown()"
                    >
                        {{ $t('trackItem.moveDown') }}
                    </ContextMenuItem>
                    <ContextMenuItem
                        v-if="currentProjectId && isInCurrentProject"
                        icon="bi bi-x-circle"
                        @click="removeTrackFromProject(file.id)"
                    >
                        {{ $t('trackItem.removeFromProject', { project: getCurrentProjectName() }) }}
                    </ContextMenuItem>
                    <ContextMenuItem v-if="projects.length > 0">
                        {{ $t('trackItem.moveToProject') }}
                        <template #submenu>
                            <ContextMenuItem
                                v-for="project in projects"
                                :key="project.id"
                                :highlighted="project.id === currentProjectId"
                                @click="moveTrackToProject(file.id, project.id)"
                            >
                                {{ project.name }} {{ project.id === currentProjectId ? $t('trackItem.currentProject') : '' }}
                            </ContextMenuItem>
                        </template>
                    </ContextMenuItem>
                </ContextMenu>
            </div>
        </div>
    </div>
</template>

<script>
import { playerActions, playerStore } from '@/scripts/services/PlayerService';
import storageService from '@/scripts/services/StorageService';
import { settings } from '@/scripts/services/SettingsService';
import ContextMenu from '@/components/UI/ContextMenu.vue';
import ContextMenuItem from '@/components/UI/ContextMenuItem.vue';
import eventBus from '@/scripts/utils/eventBus';

export default {
    name: 'TrackItem',
    components: {
        ContextMenu,
        ContextMenuItem
    },
    props: {
        file: {
            type: Object,
            required: true
        },
        currentProjectId: {
            type: [String, Number],
            default: null
        },
        playlist: {
            type: Array,
            default: () => []
        },
        playlistSource: {
            type: String,
            default: 'adHoc'
        },
        hideMenu: {
            type: Boolean,
            default: false
        },
        isShared: {
            type: Boolean,
            default: false
        },
        isSelected: {
            type: Boolean,
            default: false
        },
        hideNotes: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        hasNotes() {
            return !this.hideNotes && typeof this.file.notes === 'string' && this.file.notes.trim().length > 0;
        },
        showBPM(){
            return settings.state.showBPMInTrackView
        },
        showKey(){
            return settings.state.showKeyInTrackView
        },
        showLUFS(){
            return settings.state.showLUFSInTrackView
        },
        isFilePlaying() {
            return this.player.currentTrack?.id === this.file.id;
        },
        isPlaying() {
            return this.isFilePlaying && this.player.isPlaying;
        },
        isInCurrentProject() {
            if (!this.currentProjectId) return false;
            const currentProject = this.projects.find(p => p.id === this.currentProjectId);
            const tracks = currentProject?.tracks || [];
            return tracks.some(track => track.id === this.file.id);
        },
        isInAnyProject() {
            return this.projects.some(project => (project.tracks || []).some(track => track.id === this.file.id));
        },
        availableToAddProjects() {
            return this.projects.filter(project => !(project.tracks || []).some(track => track.id === this.file.id));
        },
        projects() {
            return storageService.state.projects
        },
        storageType() {
            return storageService.state.storageType
        },
        isAuthorized() {
            return storageService.state.isAuthorized
        },
        currentProject() {
            if (!this.currentProjectId) return null;
            return this.projects.find(p => p.id === this.currentProjectId);
        },
        currentProjectTracks() {
            if (!this.currentProject) return [];
            return [...(this.currentProject.tracks || [])].sort((a, b) => {
                const aNum = a.num !== null && a.num !== undefined ? parseInt(a.num) : Infinity;
                const bNum = b.num !== null && b.num !== undefined ? parseInt(b.num) : Infinity;
                return aNum - bNum;
            });
        },
        currentTrackIndex() {
            return this.currentProjectTracks.findIndex(t => t.id === this.file.id);
        },
        canMoveUp() {
            return this.currentProjectId && this.isInCurrentProject && this.currentTrackIndex > 0;
        },
        canMoveDown() {
            return this.currentProjectId && this.isInCurrentProject && this.currentTrackIndex < this.currentProjectTracks.length - 1;
        }
    },
    data() {
        return {
            player: playerStore,
            isSavingToLocal: false,
            isSavingToCloud: false,
            hasLocalCopy: false
        }
    },
    mounted() {
        this.refreshLocalCopyStatus();
    },
    watch: {
        'file.id'() {
            this.refreshLocalCopyStatus();
        }
    },
    methods: {
        refreshLocalCopyStatus() {
            this.hasLocalCopy = storageService.isTrackInLocalStorage(this.file.id);
        },
        goto() {
            if(!this.hideMenu){
                this.$router.push('/track/' + this.file.id)
            }
        },
        getCurrentProjectName() {
            if (!this.currentProjectId) return '';
            const currentProject = this.projects.find(p => p.id === this.currentProjectId);
            return currentProject ? currentProject.name : '';
        },
        deleteTrack(id) {
            if (confirm(this.$t('trackItem.confirmDelete'))) {
                storageService.deleteTrack(id);
            }
            this.$refs.contextMenu?.close();
        },
        moveTrackToProject(trackId, targetProjectId) {
            storageService.moveTrackToProject(trackId, targetProjectId);
            this.$refs.contextMenu?.close();
        },
        addTrackToProject(trackId, projectId) {
            storageService.addTrackToProject(trackId, projectId);
            this.$refs.contextMenu?.close();
        },
        removeTrackFromProject(trackId, projectId) {
            storageService.removeTrackFromProject(trackId, projectId || this.currentProjectId);
            this.$refs.contextMenu?.close();
        },
        playTrack() {
            playerActions.setPlaylist(this.playlist, this.currentProjectId, this.playlistSource);
            playerActions.playTrack(this.file.id);
        },
        toggle() {
            playerStore.currentProjectId = this.currentProjectId;
            playerActions.togglePlay();
        },
        openContextMenu(event) {
            if (this.hideMenu || this.isShared) return;
            event.preventDefault();
            event.stopPropagation();
            this.$refs.contextMenu?.openAt(event.clientX, event.clientY);
        },
        async copyPublicLink() {
            try {
                const existing = storageService.state.publicLinks.find(link => {
                    const linkObjectId = link.object || link.objectId
                    return link.type === 'track' && linkObjectId === this.file.id
                })
                const linkId = existing?.id
                const res = linkId ? { id: linkId } : await storageService.createLink('track', this.file.id)
                if (res && res.id) {
                    const linkUrl = "https://dash.demoparty.app/link/" + res.id
                    await navigator.clipboard.writeText(linkUrl)
                    eventBus.notify(this.$t('trackItem.linkCopiedTitle'), this.$t('trackItem.linkCopiedDescription'))
                }
            } catch (error) {
                console.error('Failed to copy public link:', error)
            }
            this.$refs.contextMenu?.close();
        },
        async saveToLocal() {
            if (this.isSavingToLocal || this.hasLocalCopy) return;
            this.isSavingToLocal = true;
            try {
                await storageService.saveTrackToLocal(this.file.id);
                this.refreshLocalCopyStatus();
            } finally {
                this.isSavingToLocal = false;
                this.$refs.contextMenu?.close();
            }
        },
        async saveToCloud() {
            if (this.isSavingToCloud) return;
            this.isSavingToCloud = true;
            try {
                await storageService.saveTrackToCloud(this.file.id);
            } finally {
                this.isSavingToCloud = false;
                this.$refs.contextMenu?.close();
            }
        },
        async moveUp() {
            if (!this.canMoveUp) return;

            const tracks = this.currentProjectTracks;
            const currentIdx = this.currentTrackIndex;
            const prevIdx = currentIdx - 1;
            const order = tracks.map(track => track.id);
            [order[prevIdx], order[currentIdx]] = [order[currentIdx], order[prevIdx]];

            try {
                await storageService.reorderProjectTracks(this.currentProjectId, order);
                this.$refs.contextMenu?.close();
            } catch (error) {
                console.error('Failed to move track up:', error);
            }
        },
        async moveDown() {
            if (!this.canMoveDown) return;

            const tracks = this.currentProjectTracks;
            const currentIdx = this.currentTrackIndex;
            const nextIdx = currentIdx + 1;
            const order = tracks.map(track => track.id);
            [order[currentIdx], order[nextIdx]] = [order[nextIdx], order[currentIdx]];

            try {
                await storageService.reorderProjectTracks(this.currentProjectId, order);
                this.$refs.contextMenu?.close();
            } catch (error) {
                console.error('Failed to move track down:', error);
            }
        },
        formatDateShort(input) {
            const date = input instanceof Date ? input : new Date(input);
            if (!Number.isFinite(date.getTime())) return '';
            return date.toLocaleDateString(this.$i18n.locale, {
                day: '2-digit',
                month: 'short'
            });
        },
        parseDuration(seconds) {
            if (!Number.isFinite(seconds)) return '00:00'

            seconds = Math.floor(seconds/1000)

            const h = Math.floor(seconds / 3600)
            const m = Math.floor((seconds % 3600) / 60)
            const s = seconds % 60

            return [
                h > 0 ? String(h).padStart(2, '0') : null,
                String(m).padStart(2, '0'),
                String(s).padStart(2, '0')
            ].filter(Boolean).join(':')
        }

    }
}
</script>

<style scoped>
.wrapper {
    display: flex;
    align-items: center;
    text-align: center;
    overflow: hidden;
}

.track_info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-align: left;
}

.track_name {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.track_local_icon {
    margin-right: 0.35rem;
    opacity: 0.75;
    vertical-align: -0.05em;
}

.track_chips {
    display: flex;
    flex-wrap: nowrap;
    gap: 0.1rem;
    overflow: hidden;
    white-space: nowrap;
}

.track_chips .chip_thing {
    flex-shrink: 0;
}

.track_chips .chip_thing_two {
    flex-shrink: 0;
    white-space: nowrap;
}

.track_chips .track_note {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
}

.right {
    margin-left: auto;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    position: relative;
}

.track {
    padding: 0.2rem 1rem 0.2rem 1rem;
    border-radius: 9px;
    transition: box-shadow 240ms cubic-bezier(.2, .8, .2, 1),
        background-color 240ms ease;
    overflow: hidden;
}

.track:hover,
.track.selected {
    background: #2F2F2F;
    box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 9px 0px;
}

.playbutt {
    margin-right: 0;
    max-width: 0;
    overflow: hidden;
    transition: transform 1000ms cubic-bezier(.16, 1, .3, 1), opacity 1000ms cubic-bezier(.16, 1, .3, 1), max-width 1000ms cubic-bezier(.16, 1, .3, 1), margin-right 1000ms cubic-bezier(.16, 1, .3, 1);
    flex-shrink: 0;
    opacity: 0;
    pointer-events: none;
}

.track:hover .playbutt,
.playbutt.playbutt-visible {
    opacity: 1;
    pointer-events: auto;
    max-width: 3rem;
    margin-right: 0.5rem;
}

.playbutt:hover {
    transform: scale(1.15);
}

.drag-over-target {
    /*Fix colors*/
    background-color: rgba(46, 46, 46, 0.1) !important;
    border: 2px dashed #6d6d6d;
    border-radius: 9px;
}

.sec {
    margin-right: 1rem;
    display: flex;
    align-items: center;
    color: #bbb;
    font-size: 0.9rem;
}
.track_duration {
    margin-right: 0.5rem;
    font-weight: 600;
    font-size: 0.9rem;
}
</style>
